/**
 * How the Submit page's files describe themselves (R3 T6a/T6b). Pure.
 *
 *   "Word · 3 Nov, 2:14 pm · 84 KB · 24 pages"   a file's meta line
 *   "Lodged 3 Nov · 5 files"                      a lodgement record
 */

import { formatFileSize } from '@/lib/schemas'
import type {
  FileCheck,
  LodgeFileInput,
  SubmissionFile,
  SubmissionFileChoice,
  SubmissionFileItem,
  SubmissionFileKind,
  SubmissionRecord,
} from '@/types/submission-files'

const KIND_LABEL: Record<SubmissionFileKind, string> = {
  response_docx: 'Word',
  response_pdf: 'PDF',
  form_docx: 'Completed form (Word)',
  form_pdf: 'Completed form (PDF)',
  external: 'Uploaded file',
  schedule_xlsx: 'Excel schedule',
}

export function fileKindLabel(kind: SubmissionFileKind): string {
  return KIND_LABEL[kind]
}

function sameYear(date: Date, now: Date): boolean {
  return date.getFullYear() === now.getFullYear()
}

/** "3 Nov" (this year) or "3 Nov 2025". */
export function shortDate(iso: string, now: Date = new Date()): string {
  const date = new Date(iso)
  return new Intl.DateTimeFormat('en-AU', {
    day: 'numeric',
    month: 'short',
    ...(sameYear(date, now) ? {} : { year: 'numeric' }),
  }).format(date)
}

/** "3 Nov, 2:14 pm". */
export function shortDateTime(iso: string, now: Date = new Date()): string {
  const time = new Intl.DateTimeFormat('en-AU', { hour: 'numeric', minute: '2-digit' }).format(new Date(iso))
  return `${shortDate(iso, now)}, ${time}`
}

export function pagesText(pageCount: number, approx: boolean): string {
  return `${approx ? 'about ' : ''}${pageCount} ${pageCount === 1 ? 'page' : 'pages'}`
}

type MetaSource = Pick<SubmissionFile, 'kind' | 'createdAt' | 'sizeBytes' | 'pageCount'>

/**
 * The file's quiet meta line. Only buyer forms say "about" for pages — a
 * response PDF's count is measured from the PDF itself (plan §9).
 */
export function fileMetaLine(file: MetaSource | SubmissionFileChoice, now: Date = new Date()): string {
  const parts = [fileKindLabel(file.kind), shortDateTime(file.createdAt, now)]
  if (file.sizeBytes !== null) parts.push(formatFileSize(file.sizeBytes))
  if (file.pageCount !== null) parts.push(pagesText(file.pageCount, file.kind === 'form_pdf'))
  return parts.join(' · ')
}

export interface CheckCounts {
  pass: number
  attention: number
  unchecked: number
}

export function countChecks(checks: readonly FileCheck[]): CheckCounts {
  return {
    pass: checks.filter((c) => c.status === 'pass').length,
    attention: checks.filter((c) => c.status === 'attention').length,
    unchecked: checks.filter((c) => c.status === 'unchecked').length,
  }
}

/** "2 to look at · 3 passed · 4 not checked" — the parts that are non-zero. */
export function checkSummary(checks: readonly FileCheck[]): string {
  const counts = countChecks(checks)
  const parts: string[] = []
  if (counts.attention > 0) parts.push(`${counts.attention} to look at`)
  if (counts.pass > 0) parts.push(`${counts.pass} passed`)
  if (counts.unchecked > 0) parts.push(`${counts.unchecked} not checked`)
  return parts.join(' · ')
}

/** "Lodged 3 Nov · 5 files". */
/**
 * "Lodged 3 Nov · 1 file". `previous` is for a bid moved back to Active or
 * Lead after lodging: the record is history, not the bid's current state.
 */
export function lodgedSummary(
  record: Pick<SubmissionRecord, 'lodgedAt' | 'files'>,
  now: Date = new Date(),
  { previous = false }: { previous?: boolean } = {}
): string {
  const count = record.files.length
  return `${previous ? 'Previously lodged' : 'Lodged'} ${shortDate(record.lodgedAt, now)} · ${count} ${count === 1 ? 'file' : 'files'}`
}

/** One freeze entry per ticked item that has a file, naming exactly the file shown. */
export function lodgeFilesFor(items: readonly SubmissionFileItem[], ticked: ReadonlySet<string>): LodgeFileInput[] {
  return items.flatMap((item): LodgeFileInput[] => {
    const file = item.file
    if (!file || !ticked.has(item.itemId)) return []
    if (file.outputVersionId) return [{ itemId: item.itemId, outputVersionId: file.outputVersionId }]
    if (file.documentId) return [{ itemId: item.itemId, documentId: file.documentId }]
    if (file.scheduleExportId) return [{ itemId: item.itemId, scheduleExportId: file.scheduleExportId }]
    return []
  })
}

/** A Date as the value of an `<input type="datetime-local">`, in the browser's time zone. */
export function toDateTimeLocalValue(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`
}

/** An `<input type="datetime-local">` value back to an ISO instant; null when empty or invalid. */
export function fromDateTimeLocalValue(value: string): string | null {
  if (!value) return null
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? null : date.toISOString()
}
